import { useEffect, useRef, useState } from "react";
import { assetUrl } from "../api/client";
import { useHands } from "../api/recordings";
import HandOverlay from "./HandOverlay";
import HandTimeline from "./HandTimeline";
import PiiChip from "./PiiChip";
import type { PiiInfo } from "../api/types";

type Tab = "overlay" | "report" | "calibration";

interface Props {
  videoUrl: string | null;
  blurredVideoUrl: string | null;
  calibrationVideoUrl: string | null;
  rrdUrl: string | null;
  handsUrl: string | null;
  reportUrl: string | null;
  durationMs: number | null;
  pii: PiiInfo | null | undefined;
  /** Share the main <video> element with the page (clipping mode scrubs/reads it). */
  videoRef?: React.RefObject<HTMLVideoElement>;
}

/**
 * The detail-page centerpiece. Tabs between an annotated <video> (hand-keypoint canvas
 * overlay), stera's interactive HTML report, and the optional QA calibration clip. The report
 * and the calibration video are lazy-mounted only when their tab is active (the report is an
 * iframe; the calibration clip is a separate mp4 stream we don't want to fetch eagerly).
 */
export default function VideoViewer({
  videoUrl,
  blurredVideoUrl,
  calibrationVideoUrl,
  handsUrl,
  reportUrl,
  durationMs,
  pii,
  videoRef: sharedVideoRef,
}: Props) {
  const [tab, setTab] = useState<Tab>("overlay");
  const [showOverlay, setShowOverlay] = useState(true);
  const [blurred, setBlurred] = useState(false);
  const ownVideoRef = useRef<HTMLVideoElement>(null);
  const videoRef = sharedVideoRef ?? ownVideoRef;
  const durationS = durationMs != null ? durationMs / 1000 : null;

  const hasBlurred = Boolean(blurredVideoUrl);
  // If the blurred copy disappears (e.g. after a reprocess), fall back to the original.
  useEffect(() => {
    if (!hasBlurred) setBlurred(false);
  }, [hasBlurred]);
  const activeVideoUrl = blurred && blurredVideoUrl ? blurredVideoUrl : videoUrl;

  // Fetch hands whenever the overlay tab is open (the timeline uses it even with the
  // keypoint overlay toggled off). Cached aggressively, so this is a one-time cost.
  const hands = useHands(tab === "overlay" ? handsUrl : null);
  const handCount = hands.data?.frames.length ?? 0;

  return (
    <section className="viewer card">
      <div className="viewer-tabs">
        <div className="tab-group">
          <button
            className={tab === "overlay" ? "vtab active" : "vtab"}
            onClick={() => setTab("overlay")}
          >
            Overlay
          </button>
          <button
            className={tab === "report" ? "vtab active" : "vtab"}
            onClick={() => setTab("report")}
            title={reportUrl ? "stera interactive report" : "No report for this recording"}
          >
            Report
          </button>
          <button
            className={tab === "calibration" ? "vtab active" : "vtab"}
            onClick={() => setTab("calibration")}
            title={
              calibrationVideoUrl
                ? "Pre-recording calibration clip (QA)"
                : "No calibration clip for this recording"
            }
          >
            Calibration
          </button>
        </div>

        {tab === "overlay" && (
          <div className="viewer-tools">
            <PiiChip pii={pii} />
            {hasBlurred && (
              <label
                className="overlay-toggle"
                title="Play the PII face-blurred copy of this recording"
              >
                <input
                  type="checkbox"
                  checked={blurred}
                  onChange={(e) => setBlurred(e.target.checked)}
                />
                Blurred (PII)
              </label>
            )}
            {handsUrl && (
              <>
                <span className="legend">
                  <span className="legend-dot left" /> L
                  <span className="legend-dot right" /> R
                </span>
                <label className="overlay-toggle">
                  <input
                    type="checkbox"
                    checked={showOverlay}
                    onChange={(e) => setShowOverlay(e.target.checked)}
                  />
                  Hand overlay
                </label>
              </>
            )}
          </div>
        )}
      </div>

      {tab === "overlay" && (
        <div className="overlay-stage">
          {activeVideoUrl ? (
            <div className="video-frame">
              <video
                ref={videoRef}
                className="review-video"
                controls
                playsInline
                preload="metadata"
                src={assetUrl(activeVideoUrl)}
              />
              {showOverlay && hands.data && (
                <HandOverlay videoRef={videoRef} hands={hands.data} enabled={showOverlay} />
              )}
            </div>
          ) : (
            <div className="viewer-empty">No video available for this recording.</div>
          )}
          {activeVideoUrl && hands.data && hands.data.frames.length > 0 && (
            <HandTimeline videoRef={videoRef} hands={hands.data} durationS={durationS} />
          )}
          <div className="overlay-status">
            {!handsUrl && <span className="muted">No hand keypoints for this recording.</span>}
            {handsUrl && hands.isLoading && (
              <span className="muted">Loading hand keypoints…</span>
            )}
            {handsUrl && hands.isError && (
              <span className="muted">Could not load hand keypoints.</span>
            )}
            {handsUrl && hands.data && (
              <span className="muted">
                {handCount.toLocaleString()} annotated frames · {hands.data.image_width}×
                {hands.data.image_height}
              </span>
            )}
          </div>
        </div>
      )}

      {tab === "report" && (
        <div className="report-stage">
          {reportUrl ? (
            <>
              <div className="report-bar">
                <span className="muted small">
                  stera interactive report — loads external map/plot assets.
                </span>
                <a
                  className="btn small"
                  href={assetUrl(reportUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in new tab ↗
                </a>
              </div>
              {/* App-wide COOP/COEP is intentionally OFF, so the report's cross-origin
                  Plotly/MapLibre assets load in a plain iframe — no `credentialless` needed. */}
              <iframe
                className="report-frame"
                src={assetUrl(reportUrl)}
                title="stera interactive report"
              />
            </>
          ) : (
            <div className="viewer-empty">No report for this recording.</div>
          )}
        </div>
      )}

      {tab === "calibration" && (
        <div className="overlay-stage">
          {calibrationVideoUrl ? (
            <>
              <div className="report-bar">
                <span className="muted small">
                  Pre-recording calibration clip captured by the app (QA diagnostic).
                </span>
                <a
                  className="btn small"
                  href={assetUrl(calibrationVideoUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in new tab ↗
                </a>
              </div>
              <div className="video-frame">
                <video
                  className="review-video"
                  controls
                  playsInline
                  preload="metadata"
                  src={assetUrl(calibrationVideoUrl)}
                />
              </div>
            </>
          ) : (
            <div className="viewer-empty">No calibration clip for this recording.</div>
          )}
        </div>
      )}
    </section>
  );
}
